import { DataPipeline, DbTable } from '@finley-technologies/finley-types';

import { useUploadContext } from 'packages/dataPipeline/hooks/UploadContext';

export type NodeProgress = {
  // if node instance is available, the status will be server status.
  status: DbTable.ArtifactInstance.ArtifactInstanceStatus | null;

  // how many total (recursive) inputs are needed for this node
  total: number;

  // how many of those inputs are complete?
  complete: number;
};

const getInputs = ({
  artifactId,
  meshDocument,
}: {
  artifactId: string;
  meshDocument: DataPipeline.MeshDocument;
}): string[] => {
  return meshDocument.dependencies
    .filter(({ target }) => target === artifactId)
    .map(({ source }) => source);
};

// returns all inputs for the given artifactId
const getRecursiveInputs = ({
  artifactId,
  meshDocument,
}: {
  artifactId: string;
  meshDocument: DataPipeline.MeshDocument;
}): string[] => {
  const directInputs = getInputs({ meshDocument, artifactId });
  const recursiveInputs = directInputs.flatMap((input) =>
    getRecursiveInputs({ meshDocument, artifactId: input }),
  );

  return Array.from(new Set([...directInputs, ...recursiveInputs]));
};

export const useNodeProgress = ({
  artifactId,
}: {
  artifactId: string;
}): NodeProgress => {
  const uploadContext = useUploadContext();

  const allInputs = getRecursiveInputs({
    artifactId,
    meshDocument: uploadContext.meshDocument,
  });

  const complete = allInputs.filter((input) =>
    uploadContext.instances.find((instance) => instance.artifactId === input),
  ).length;

  const foundInstance = uploadContext.instances.find(
    (instance) => instance.artifactId === artifactId,
  );

  const status = foundInstance?.status ?? null;
  return {
    status,
    complete,
    total: allInputs.length,
  };
};

export const usePipelineProgress = (): {
  totalNodes: number;
  completedNodes: number;
} => {
  const uploadContext = useUploadContext();

  const totalNodes = uploadContext.meshDocument.artifacts.length;
  const completedNodes = uploadContext.instances.length;

  return {
    totalNodes,
    completedNodes,
  };
};
